// DEV-9883: checkout exposure ordering shim.
//
// PostHog experiments only count metric events with `timestamp >=` the
// person's FIRST `$feature_flag_called` exposure, and the live LP
// experiment uses the checkout `$pageview` as its primary-metric
// denominator: the LP checkout since DEV-9829, the store checkout
// (`/{market}/checkout`) too since DEV-10696. The existing exposure path
// (Vv5ExposureBeacon) runs in an effect that fires AFTER the `$pageview`
// capture in StoreTrackingScripts (measured at ~4 ms late in 65% of sessions),
// so the exposure-pageload's checkout pageview is excluded from the
// denominator.
//
// This helper is called synchronously right before the `$pageview` capture.
// Both calls enqueue into the same FIFO queue in scripts/posthog.tsx, so the
// exposure timestamp is <= the pageview timestamp regardless of PostHog init
// state. It fires ONLY the PostHog pair (super property + exposure event),
// not `split_test` / trackAction, so datalake/GA/Bloomreach are untouched.
// That's why it calls `firePostHogExposure` rather than the full
// `trackExperimentExposure`; the two keep separate dedupe Sets, so firing here
// does not suppress `Vv5ExposureBeacon`'s later Bloomreach/datalake leg.
// Duplicate `$feature_flag_called` from the existing paths is harmless:
// PostHog attributes on the first exposure per person and ignores
// same-variant repeats.

import Cookies from "js-cookie"
import { STAGE } from "@local/utils/src/services"
import { firePostHogExposure } from "./exposure"
import {
  isFreeShippingLpChannel,
  isValidVv5Variant,
  VV5_COOKIE,
  VV5_FEATURE_FLAG,
} from "./freeShipping"

/**
 * Fire experiment exposure for checkout pageloads (enrolled LP checkouts and
 * the store checkout), to be called immediately BEFORE the `$pageview` capture
 * so the pageview counts in exposure-gated experiment metrics.
 *
 * @param pathname - The current `window.location.pathname`
 */
const fireCheckoutExposure = (pathname: string) => {
  if (typeof window === "undefined") {
    return
  }

  const segments = pathname.split("/").filter(Boolean)

  // [VV5-TEARDOWN] lp-free-shipping (DEV-9829, store checkout since
  // DEV-10696): remove this block at teardown.
  //
  // Exactly /{market}/lp/{enrolled channel}/checkout, or exactly
  // /{market}/checkout: length checks so catch-all 404s under /checkout/*
  // can't emit an exposure. `TrackingLoader` is not mounted in mirror-anchor
  // and the cookie is host-scoped, so no other app can reach the store branch.
  const [, section, channel, subpage] = segments
  const isLpCheckout =
    segments.length === 4 &&
    section === "lp" &&
    subpage === "checkout" &&
    isFreeShippingLpChannel(channel)
  const isStoreCheckout = segments.length === 2 && section === "checkout"

  if (isLpCheckout || isStoreCheckout) {
    const vv5 = Cookies.get(VV5_COOKIE)
    // A VALID `?vv5=` override marks a QA session, so don't enroll it. Only
    // off-prd though: the middleware ignores the override on production
    // (`middleware/freeShipping.ts` STAGE gate), so a prd visitor carrying
    // `?vv5=` is still a real cookie-bucketed participant and MUST count.
    // Bogus override values fall through to a real bucket and count too.
    const override =
      STAGE !== "prd"
        ? new URLSearchParams(window.location.search).get(VV5_COOKIE)
        : null
    if (isValidVv5Variant(vv5) && !isValidVv5Variant(override)) {
      firePostHogExposure({ flag: VV5_FEATURE_FLAG, variant: vv5 })
    }
  }
}

export { fireCheckoutExposure }
